import {
	Box,
	Typography,
	TextField,
	MenuItem,
	Autocomplete
} from '@mui/material'
import DropdownCalendar from './ui/DropdownCalendar'
import { LavDropdown } from './ui/LavDropdown'
import { petTypes, dogBreedList, catBreedList } from '../utils/breedAndPetTypeUtils'

// Upper bound for date of birth. Built from local calendar parts —
// toISOString() is UTC, which caps a day early for timezones behind it.
const todayStr = () => {
	const d = new Date()
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Nami lavender theme tokens (from the design handoff)
const CMF = {
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	danger: '#BE4B45'
}
const FONTF = "'Outfit', sans-serif"

// Outlined field styling matching the Nami cm-input / cm-select look
const namiInputSx = {
	'& .MuiOutlinedInput-root': {
		bgcolor: CMF.field,
		borderRadius: '8px',
		fontFamily: FONTF,
		fontSize: '13.5px',
		color: CMF.text,
		'& fieldset': { borderColor: CMF.border, borderWidth: '1.5px' },
		'&:hover fieldset': { borderColor: CMF.primary },
		'&.Mui-focused fieldset': { borderColor: CMF.primary, borderWidth: '1.5px' },
		'& input::placeholder, & textarea::placeholder': { color: CMF.muted, opacity: 1 }
	},
	// Single-line inputs: match the 42px LavDropdown height (textareas stay auto).
	'& input.MuiOutlinedInput-input': {
		height: '42px',
		paddingTop: 0,
		paddingBottom: 0,
		boxSizing: 'border-box'
	}
}

const NamiFieldLabel = ({ children, required }) => (
	<Typography sx={{ fontFamily: FONTF, fontSize: '12px', fontWeight: 650, color: CMF.text }}>
		{children}
		{required && <Box component="span" sx={{ color: CMF.danger, ml: '2px' }}>*</Box>}
	</Typography>
)

/**
 * Shared pet form component for add/edit pet functionality
 * @param {Object} formData - The form data object
 * @param {Function} setFormData - Function to update form data
 * @param {boolean} compact - If true, uses compact styling (for dialogs)
 * @param {boolean} showLabels - If true, shows field labels above inputs
 */
function PetForm({
	formData,
	setFormData,
	compact = false,
	showLabels = true,
	layout = 'default',
	section = 'all',
	// Force the stacked (mobile) grid regardless of viewport — used when the
	// grid layout renders inside a narrow container (e.g. the desktop
	// add-appointment side panel) where viewport breakpoints would otherwise
	// pick the wide desktop columns.
	stack = false
}) {
	const handleChange = (field) => (event) => {
		setFormData(prev => ({
			...prev,
			[field]: event.target.value
		}))
	}

	const handleSpeciesChange = (event, newValue) => {
		setFormData(prev => ({
			...prev,
			species: newValue || '',
			breed: '' // Reset breed when species changes
		}))
	}

	const handleBreedChange = (event, newValue) => {
		setFormData(prev => ({
			...prev,
			breed: newValue || ''
		}))
	}

	const getBreedOptions = () => {
		if (formData.species === 'Dog') return dogBreedList
		if (formData.species === 'Cat') return catBreedList
		return []
	}

	// Nami "Edit pet · profile form" grid layout (AddEditPetPage desktop)
	if (layout === 'grid') {
		const secTitleSx = {
			fontFamily: FONTF, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase',
			letterSpacing: '0.08em', color: CMF.primaryDeep, mb: '12px'
		}
		const identityBlock = (
			<Box>
				<Typography sx={secTitleSx}>Identity</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: stack ? '1fr' : { xs: '1fr', sm: '1fr 1fr' }, gap: '16px', mb: '16px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel required>Pet name</NamiFieldLabel>
						<TextField
							fullWidth
							value={formData.name}
							onChange={handleChange('name')}
							placeholder="e.g. Nami"
							sx={namiInputSx}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel>Breed</NamiFieldLabel>
						<LavDropdown
							placeholder={formData.species ? `Search ${formData.species.toLowerCase()} breeds` : 'Select pet type first'}
							value={formData.breed || null}
							options={getBreedOptions().map((b) => ({ id: b, label: b }))}
							onSelect={(id) => handleBreedChange(null, id)}
							disabled={!formData.species}
							clearable
							searchable
						/>
					</Box>
				</Box>
				<Box sx={{ display: 'grid', gridTemplateColumns: stack ? '1fr 1fr' : { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: '16px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel>Species</NamiFieldLabel>
						<LavDropdown
							placeholder="Select species"
							value={formData.species || null}
							options={petTypes.map((t) => ({ id: t, label: t }))}
							onSelect={(id) => handleSpeciesChange(null, id)}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel>Sex</NamiFieldLabel>
						<LavDropdown
							placeholder="Select sex"
							value={formData.sex || null}
							options={[{ id: 'Male', label: 'Male' }, { id: 'Female', label: 'Female' }]}
							onSelect={(id) => setFormData((prev) => ({ ...prev, sex: id || '' }))}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel>Weight</NamiFieldLabel>
						<TextField
							fullWidth
							type="number"
							value={formData.weight}
							onChange={handleChange('weight')}
							placeholder="0"
							InputProps={{ endAdornment: <Typography sx={{ fontFamily: FONTF, fontSize: '13px', fontWeight: 650, color: CMF.muted, whiteSpace: 'nowrap', pl: '6px', flexShrink: 0 }}>lbs</Typography> }}
							inputProps={{ step: '0.1', min: '0' }}
							sx={{ ...namiInputSx, '& .MuiOutlinedInput-root': { ...namiInputSx['& .MuiOutlinedInput-root'], pr: '10px' }, '& input': { minWidth: 0 } }}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
						<NamiFieldLabel>Date of birth</NamiFieldLabel>
						<DropdownCalendar
							value={formData.birthday || ''}
							onChange={(v) => setFormData((prev) => ({ ...prev, birthday: v }))}
							placeholder="Select date"
							align="right"
							max={todayStr()}
						/>
					</Box>
				</Box>
			</Box>
		)

		const careNotesBlock = (
			<Box>
				<Typography sx={secTitleSx}>Care notes</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: stack ? '1fr' : { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
					{[
						{ field: 'medical_info', label: 'Medical', placeholder: 'Allergies, conditions, medications, vaccination status…' },
						{ field: 'grooming_info', label: 'Grooming', placeholder: 'Coat type, sensitivities, preferred products, handling notes…' },
						{ field: 'personality', label: 'Personality', placeholder: 'Temperament, what calms them, what to avoid…' }
					].map((n) => (
						<Box key={n.field} sx={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
							<NamiFieldLabel>{n.label}</NamiFieldLabel>
							<TextField
								fullWidth
								multiline
								minRows={4}
								value={formData[n.field]}
								onChange={handleChange(n.field)}
								placeholder={n.placeholder}
								sx={namiInputSx}
							/>
						</Box>
					))}
				</Box>
			</Box>
		)

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
				{section !== 'notes' && identityBlock}
				{section === 'all' && <Box sx={{ height: '1px', bgcolor: CMF.border }} />}
				{section !== 'identity' && careNotesBlock}
			</Box>
		)
	}

	// Compact styling for dialogs (PetFormDialog)
	if (compact) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{/* Row 1: Name and Pet Type */}
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
						<TextField
							fullWidth
							label="Pet Name *"
							value={formData.name}
							onChange={handleChange('name')}
							placeholder="Enter pet's name"
						/>
					</Box>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
						<Autocomplete
							fullWidth
							options={petTypes}
							value={formData.species || null}
							onChange={handleSpeciesChange}
							sx={{
								'& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
								'& .MuiAutocomplete-input': { padding: '16.5px 14px !important' }
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Pet Type *"
									placeholder="Select pet type"
								/>
							)}
						/>
					</Box>
				</Box>

				{/* Row 2: Breed and Sex */}
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
						<Autocomplete
							fullWidth
							options={getBreedOptions()}
							value={formData.breed || null}
							onChange={handleBreedChange}
							disabled={!formData.species}
							sx={{
								'& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
								'& .MuiAutocomplete-input': { padding: '16.5px 14px !important' }
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Breed"
									placeholder={formData.species ? `Select ${formData.species.toLowerCase()} breed` : "Select pet type first"}
								/>
							)}
						/>
					</Box>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
						<TextField
							select
							fullWidth
							label="Sex"
							value={formData.sex}
							onChange={handleChange('sex')}
							sx={{ width: '100%', minWidth: '100px', '& .MuiInputBase-root': { width: '100%' }, '& .MuiSelect-select': { width: '100%' } }}
						>
							<MenuItem value="">
								<em>Not specified</em>
							</MenuItem>
							<MenuItem value="Male">Male</MenuItem>
							<MenuItem value="Female">Female</MenuItem>
						</TextField>
					</Box>
				</Box>

				{/* Row 3: Birthday and Weight */}
				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
						<Typography sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)' }}>Birthday</Typography>
						<DropdownCalendar
							value={formData.birthday || ''}
							onChange={(v) => setFormData((prev) => ({ ...prev, birthday: v }))}
							placeholder="Select pet's birthday"
							max={todayStr()}
						/>
					</Box>
					<Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
						<TextField
							fullWidth
							label="Weight (lbs)"
							type="number"
							value={formData.weight}
							sx={{ minWidth: "110px" }}
							onChange={handleChange('weight')}
							placeholder="Enter weight in pounds"
							inputProps={{
								step: "0.1",
								min: "0",
								max: "999.99"
							}}
						/>
					</Box>
				</Box>

				{/* Medical Info */}
				<TextField
					fullWidth
					label="Medical Info"
					size="small"
					value={formData.medical_info}
					onChange={handleChange('medical_info')}
					placeholder="Allergies, medications, vaccinations, conditions..."
					multiline
					minRows={2}
					sx={{ '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: 'none' } }}
				/>

				{/* Grooming Info */}
				<TextField
					fullWidth
					label="Grooming Info"
					size="small"
					value={formData.grooming_info}
					onChange={handleChange('grooming_info')}
					placeholder="Preferences, sensitivities, grooming notes..."
					multiline
					minRows={2}
					sx={{ '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: 'none' } }}
				/>

				{/* Personality */}
				<TextField
					fullWidth
					label="Personality"
					size="small"
					value={formData.personality}
					onChange={handleChange('personality')}
					placeholder="Temperament, likes/dislikes, behavior notes..."
					multiline
					minRows={2}
					sx={{ '& .MuiInputBase-inputMultiline': { overflow: 'hidden', resize: 'none' } }}
				/>
			</Box>
		)
	}

	// Full styling with labels (AddEditPetPage)
	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			alignSelf: 'stretch',
			gap: '8px'
		}}>
			{/* Name */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Name
						<Typography component="span" sx={{ color: '#E53E3E', ml: '2px' }}>
							*
						</Typography>
					</Typography>
				)}
				<Box sx={{
					display: 'flex',
					justifyContent: 'stretch',
					alignItems: 'center',
					alignSelf: 'stretch',
					gap: '10px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px',
					minHeight: 'auto'
				}}>
					<TextField
						fullWidth
						value={formData.name}
						onChange={handleChange('name')}
						placeholder="Placeholder"
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '14px',
								height: '17px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								'& input::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
						sx={{
							'& .MuiInputBase-root': {
								p: 0,
								minHeight: 0
							},
							'& input': {
								p: 0,
								minHeight: 0
							}
						}}
					/>
				</Box>
			</Box>

			{/* Pet Type */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Pet Type
					</Typography>
				)}
				<Autocomplete
					fullWidth
					options={petTypes}
					value={formData.species || null}
					onChange={handleSpeciesChange}
					sx={{
						'& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
						'& .MuiAutocomplete-input': { padding: '12px 16px !important' }
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="Select pet type"
							variant="outlined"
							sx={{
								'& .MuiOutlinedInput-root': {
									bgcolor: '#FFFFFF',
									border: '1px solid #CBD5E0',
									borderRadius: '6px',
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '14px',
									'& fieldset': {
										border: 'none'
									},
									'& input::placeholder': {
										color: '#CBD5E0',
										opacity: 1
									}
								}
							}}
						/>
					)}
				/>
			</Box>

			{/* Breed */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Breed
					</Typography>
				)}
				<Autocomplete
					fullWidth
					options={getBreedOptions()}
					value={formData.breed || null}
					onChange={handleBreedChange}
					disabled={!formData.species}
					sx={{
						'& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
						'& .MuiAutocomplete-input': { padding: '12px 16px !important' }
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder={formData.species ? `Select ${formData.species.toLowerCase()} breed` : "Select pet type first"}
							variant="outlined"
							sx={{
								'& .MuiOutlinedInput-root': {
									bgcolor: '#FFFFFF',
									border: '1px solid #CBD5E0',
									borderRadius: '6px',
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '14px',
									'& fieldset': {
										border: 'none'
									},
									'& input::placeholder': {
										color: '#CBD5E0',
										opacity: 1
									}
								}
							}}
						/>
					)}
				/>
			</Box>

			{/* Date of birth */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Date of birth
					</Typography>
				)}
				<DropdownCalendar
					value={formData.birthday || ''}
					onChange={(v) => setFormData((prev) => ({ ...prev, birthday: v }))}
					placeholder="Select date"
					max={todayStr()}
				/>
			</Box>

			{/* Sex Dropdown */}
			<Box sx={{
				display: 'flex',
				alignItems: 'center',
				alignSelf: 'stretch',
				marginTop: '10px',
				gap: '10px',
				padding: '12px 16px',
				bgcolor: '#FFFFFF',
				border: '1px solid #CBD5E0',
				borderRadius: '6px'
			}}>
				{/* Sex Icon */}
				<Box sx={{ width: '16px', height: '16px', flexShrink: 0 }}>
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M6.66669 13.3333H9.33335M8.00002 10.6666V14.6666M8.00002 10.6666C9.84097 10.6666 11.3334 9.1742 11.3334 7.33325C11.3334 5.4923 9.84097 3.99992 8.00002 3.99992C6.15907 3.99992 4.66669 5.4923 4.66669 7.33325C4.66669 9.1742 6.15907 10.6666 8.00002 10.6666ZM11.3334 1.33325H14M14 1.33325V3.99992M14 1.33325L10.36 4.97325" stroke="#2D3748" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</Box>
				<TextField
					select
					fullWidth
					value={formData.sex}
					onChange={handleChange('sex')}
					displayEmpty
					variant="standard"
					SelectProps={{
						displayEmpty: true,
						renderValue: (selected) => {
							if (!selected) {
								return <span style={{ color: '#2D3748' }}>Select sex</span>
							}
							return selected
						},
						IconComponent: () => (
							<Box sx={{ width: '16px', height: '16px', flexShrink: 0, mr: 0 }}>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M4 6L8 10L12 6" stroke="#2D3748" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</Box>
						)
					}}
					InputProps={{
						disableUnderline: true,
						sx: {
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: '1.21em',
							letterSpacing: '-1.786%',
							height: '17px',
							color: '#2D3748'
						}
					}}
					sx={{
						'& .MuiInputBase-root': {
							p: 0
						},
						'& .MuiSelect-select': {
							p: 0,
							pr: '20px !important'
						}
					}}
				>
					<MenuItem value="Male">Male</MenuItem>
					<MenuItem value="Female">Female</MenuItem>
				</TextField>
			</Box>

			{/* Weight */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Weight (lbs)
					</Typography>
				)}
				<Box sx={{
					display: 'flex',
					justifyContent: 'stretch',
					alignItems: 'center',
					alignSelf: 'stretch',
					gap: '10px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px',
					minHeight: 'auto'
				}}>
					<TextField
						fullWidth
						type="number"
						value={formData.weight}
						onChange={handleChange('weight')}
						placeholder="Placeholder"
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								height: '17px',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								'& input::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
						sx={{
							'& .MuiInputBase-root': {
								p: 0,
								minHeight: 0
							},
							'& input': {
								p: 0,
								minHeight: 0
							}
						}}
						inputProps={{
							step: "0.1",
							min: "0"
						}}
					/>
				</Box>
			</Box>

			{/* Medical info */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Medical info
					</Typography>
				)}
				<Box sx={{
					display: 'flex',
					justifyContent: 'stretch',
					alignItems: 'flex-start',
					alignSelf: 'stretch',
					gap: '10px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px',
					minHeight: '51px'
				}}>
					<TextField
						fullWidth
						multiline
						minRows={1}
						value={formData.medical_info}
						onChange={handleChange('medical_info')}
						placeholder="Placeholder"
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								'& textarea::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
						sx={{
							'& .MuiInputBase-root': {
								p: 0,
								minHeight: 0
							},
							'& textarea': {
								p: 0,
								minHeight: 0
							}
						}}
					/>
				</Box>
			</Box>

			{/* Grooming info */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Grooming info
					</Typography>
				)}
				<Box sx={{
					display: 'flex',
					justifyContent: 'stretch',
					alignItems: 'flex-start',
					alignSelf: 'stretch',
					gap: '10px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px',
					minHeight: '51px'
				}}>
					<TextField
						fullWidth
						multiline
						minRows={1}
						value={formData.grooming_info}
						onChange={handleChange('grooming_info')}
						placeholder="Placeholder"
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								'& textarea::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
						sx={{
							'& .MuiInputBase-root': {
								p: 0,
								minHeight: 0
							},
							'& textarea': {
								p: 0,
								minHeight: 0
							}
						}}
					/>
				</Box>
			</Box>

			{/* Personality */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignSelf: 'stretch',
				gap: '4px'
			}}>
				{showLabels && (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Personality
					</Typography>
				)}
				<Box sx={{
					display: 'flex',
					justifyContent: 'stretch',
					alignItems: 'flex-start',
					alignSelf: 'stretch',
					gap: '10px',
					padding: '12px 16px',
					bgcolor: '#FFFFFF',
					border: '1px solid #CBD5E0',
					borderRadius: '6px',
					minHeight: '51px'
				}}>
					<TextField
						fullWidth
						multiline
						minRows={1}
						value={formData.personality}
						onChange={handleChange('personality')}
						placeholder="Placeholder"
						variant="standard"
						InputProps={{
							disableUnderline: true,
							sx: {
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								'& textarea::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
						sx={{
							'& .MuiInputBase-root': {
								p: 0,
								minHeight: 0
							},
							'& textarea': {
								p: 0,
								minHeight: 0
							}
						}}
					/>
				</Box>
			</Box>
		</Box>
	)
}

export default PetForm

